import { Component, Element, Event, EventEmitter, Host, JSX, Prop, h } from '@stencil/core'
import MarkdownIt from 'markdown-it'

import { KeyCode } from '../../enums/keycode.enum'
import { isMobile } from '../../utils/matchMedia'
import { isUndefined } from '../../utils/helpers'

@Component({
  tag: 'air-info-box',
  styleUrl: 'air-info-box.scss',
  shadow: true,
})
export class AirInfoBox {
  /** Reference to the host element */
  @Element() host!: HTMLAirInfoBoxElement

  /** Info box title */
  @Prop() text?: string

  /** Markdown data */
  @Prop() markdownData?: string[] | undefined

  /** Specifies what icon from asset library is shown for closing the modal/info-box */
  @Prop() iconClose = 'x'

  /** Specifies if the close icon is shown */
  @Prop() dismissible = false

  /** Defines the modal open state */
  @Prop({ mutable: true, reflect: true }) opened = false

  /** Defines the layout view as modal or inline */
  @Prop() isModal: boolean = isMobile()

  /** Triggers when info-box modal is closed */
  @Event({ eventName: 'air-close' }) close!: EventEmitter<void>

  private handleClose = (): void => {
    const event = this.close.emit()

    if (event.defaultPrevented) {
      return
    }

    this.opened = false
  }

  private renderMarkdown(): JSX.Element {
    const markdown =
      this.markdownData && this.markdownData.map((h: string) => MarkdownIt().render(h)).join('')
    return <div class="info-box-markdown" innerHTML={markdown}></div>
  }

  private renderInfoBox(): JSX.Element {
    return (
      <div class="info-box">
        <div class="info-box-content">
          {(this.text || this.dismissible) && (
            <span class="info-box-heading">
              {this.text}
              {this.dismissible && (
                <div
                  role="button"
                  class="info-box-close"
                  aria-label="close"
                  onClick={this.handleClose}
                >
                  {this.renderCloseIcon()}
                </div>
              )}
            </span>
          )}
          {this.markdownData && this.renderMarkdown()}
        </div>
      </div>
    )
  }

  private renderCloseIcon(): JSX.Element | null {
    if (isUndefined(this.iconClose) || this.iconClose === '') {
      return <air-icon name="x"></air-icon>
    }
    return <air-icon name={this.iconClose}></air-icon>
  }

  private handleKeyDown = (event: KeyboardEvent): void => {
    if (event.code === KeyCode.Escape) {
      this.handleClose()
    }
  }

  render(): JSX.Element {
    return (
      <Host onKeyDown={this.handleKeyDown}>
        {!this.isModal && <div class="info-box-inline">{this.renderInfoBox()}</div>}
        {this.isModal && (
          <div class="info-box-modal">
            <air-modal
              dismissible={this.dismissible}
              opened={this.opened}
              icon-close={this.iconClose}
              onAir-close={this.handleClose}
            >
              <span slot="header" class="info-box-heading">
                {this.text}
              </span>
              {this.markdownData && this.renderMarkdown()}
            </air-modal>
          </div>
        )}
      </Host>
    )
  }
}
